import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import ReferenceLiteName from 'reference-resolvers-lite/components/ReferenceLiteName';
import { PIPELINE } from 'reference-resolvers-lite/constants/ReferenceTypes';
import DataWellsComponent from 'ui-deal-management-components-lib/components/dataWells/DataWells';
import useFetchChangeIndicators from '../../hooks/useFetchChangeIndicators';
import { DEAL_OBJECT_ID } from '../../constants/CrmSearchConstants';
import { CLOSED_WON, GAP, FORECAST_SUBMISSION, shouldFetchChangeIndicators, WEEK_OVER_WEEK } from '../../constants/ChangeIndicators';
import { deriveGapChangeIndicatorValues } from '../../utils/changeIndicatorUtils';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DataWells = ({
  openPipelineCoverage,
  openPipelineValue,
  formattedTimePeriod,
  isAllPipelines,
  isDataFetching,
  name,
  singleSelectedPipelineId,
  timePeriod,
  totals
}) => {
  const goal = totals.goal;
  const goalValue = goal ? goal.value : 0;
  const goalCurrencyCode = goal ? goal.currencyCode : '';
  const closedWon = totals.closed;
  const closedWonValue = closedWon ? closedWon.value : 0;
  const closedWonCurrencyCode = closedWon ? closedWon.currencyCode : '';
  const teamSubmissionAmount = totals.teamSubmissionAmount && totals.teamSubmissionAmount.value ? totals.teamSubmissionAmount : null;
  const forecast = teamSubmissionAmount || totals.forecastValue || {
    currencyCode: '',
    value: 0
  };
  const gap = goalValue - closedWonValue;
  const {
    changeIndicatorsLoading,
    closedWonChange,
    submissionChange
  } = useFetchChangeIndicators(isDataFetching, forecast.value);
  const closedWonChangeIndicatorProps = Object.assign({}, closedWonChange, {
    isPercentChange: true
  });
  const gapChangeIndicatorProps = Object.assign({}, closedWonChange && deriveGapChangeIndicatorValues(closedWonChange, goalValue), {
    isPercentChange: true
  });
  const submissionChangeIndicatorProps = Object.assign({}, submissionChange, {
    isPercentChange: true
  });
  const shouldShowClosedWonChangeIndicator = shouldFetchChangeIndicators({
    dataWell: CLOSED_WON,
    comparisonFilter: WEEK_OVER_WEEK,
    timePeriod
  });
  const shouldShowGapChangeIndicator = shouldFetchChangeIndicators({
    dataWell: GAP,
    comparisonFilter: WEEK_OVER_WEEK,
    timePeriod
  });
  const shouldShowSubmissionChangeIndicator = shouldFetchChangeIndicators({
    dataWell: FORECAST_SUBMISSION,
    comparisonFilter: WEEK_OVER_WEEK,
    timePeriod
  });
  const goalTitle = isAllPipelines ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "repOverviewTable.dataWells.goalAllPipelines"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "repOverviewTable.dataWells.goal"
  });
  const forecastSubmissionTitle = isAllPipelines ? /*#__PURE__*/_jsx(TruncateString, {
    useFlex: true,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "repOverviewTable.dataWells.forecastSubmissionAllPipelines"
    })
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "repOverviewTable.dataWells.forecastSubmission"
  });
  const leftWells = [{
    key: 'goal',
    helpText: formattedTimePeriod,
    title: goalTitle,
    currencyCode: goalCurrencyCode,
    value: goalValue
  }, Object.assign({}, shouldShowClosedWonChangeIndicator && closedWonChangeIndicatorProps, {
    key: CLOSED_WON,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "repOverviewTable.dataWells.closedWon"
    }),
    currencyCode: closedWonCurrencyCode,
    value: closedWonValue
  }),
  // if the goal and closed won have different currency codes, gap value
  // is not accurate and should not be displayed
  ...(goalCurrencyCode === closedWonCurrencyCode ? [Object.assign({}, shouldShowGapChangeIndicator && gapChangeIndicatorProps, {
    key: GAP,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "repOverviewTable.dataWells.gap"
    }),
    currencyCode: goalCurrencyCode,
    value: gap > 0 ? gap : 0
  })] : []), Object.assign({}, shouldShowSubmissionChangeIndicator && submissionChangeIndicatorProps, {
    key: FORECAST_SUBMISSION,
    title: forecastSubmissionTitle,
    currencyCode: forecast.currencyCode,
    value: forecast.value
  })];
  const rightWells = [Object.assign({
    key: 'pipelineValue',
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "repOverviewTable.dataWells.openPipeline"
    })
  }, openPipelineValue), {
    key: 'coverage',
    title: /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(TruncateString, {
        fixedChildren: /*#__PURE__*/_jsx(HelpIcon, {
          marginInlineStart: "100",
          title: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx("b", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "repOverviewTable.dataWells.openPipelineCoverageTooltip.title"
              })
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "repOverviewTable.dataWells.openPipelineCoverageTooltip.description"
            })]
          })
        }),
        useFlex: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "repOverviewTable.dataWells.openPipelineCoverage"
        })
      })
    }),
    displayValue: openPipelineCoverage ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedNumber, {
        precision: 1,
        value: openPipelineCoverage
      }), "X"]
    }) : '—',
    value: openPipelineCoverage !== null && openPipelineCoverage !== void 0 ? openPipelineCoverage : undefined // TODO: Need to edit this type in ui-deal-management-components-lib
  }];
  const isLoading = changeIndicatorsLoading || isDataFetching;
  const pipelinesTitle = isAllPipelines ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "repOverviewTable.dataWells.allPipelines"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "repOverviewTable.dataWells.selectedPipelines"
  });
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "stretch",
    marginBlockEnd: "600",
    marginBlockStart: "200",
    maxInlineSize: "2151px",
    children: [/*#__PURE__*/_jsx(Box, {
      grow: 3,
      marginInlineEnd: "500",
      children: /*#__PURE__*/_jsx(DataWellsComponent, {
        isLoading: isLoading,
        title: name,
        wells: leftWells
      })
    }), /*#__PURE__*/_jsx(Box, {
      grow: 1,
      children: /*#__PURE__*/_jsx(DataWellsComponent, {
        helpText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "repOverviewTable.dataWells.pipelineMetricsHelpText"
        }),
        isLoading: isLoading,
        title:
        // edge case: when portal only has one pipeline, singleSelectedPipeline exists
        // but when in forecast category view we want to show All Pipelines
        singleSelectedPipelineId && !isAllPipelines ? /*#__PURE__*/_jsx("span", {
          "data-vr-hidden": "true",
          children: /*#__PURE__*/_jsx(ReferenceLiteName, {
            id: singleSelectedPipelineId,
            objectTypeId: DEAL_OBJECT_ID,
            referenceType: PIPELINE
          })
        }) : pipelinesTitle,
        wells: rightWells
      })
    })]
  });
};
export default DataWells;